import { Dialog, DialogPanel, Transition } from '@headlessui/react'
import { Fragment, useState } from 'react'

interface CancellationModalProps {
  open: boolean
  onClose: () => void
  onConfirm: (reason?: string) => Promise<void>
  policyMessage: string
  meetingTitle: string
  meetingDateTime: string
  isProcessing: boolean
}

export default function CancellationModal({
  open,
  onClose,
  onConfirm,
  policyMessage,
  meetingTitle,
  meetingDateTime,
  isProcessing
}: CancellationModalProps) {
  const [reason, setReason] = useState('')

  const handleConfirm = async () => {
    await onConfirm(reason)
  }

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={isProcessing ? () => {} : onClose}>
        <Transition.Child as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0" style={{ background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(12px) saturate(120%)' }} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto px-4 py-6">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 translate-y-2 scale-[0.985]" enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 scale-100" leaveTo="opacity-0 translate-y-2 scale-[0.985]"
            >
              <DialogPanel
                className="w-full max-w-[480px] overflow-hidden rounded-2xl relative"
                style={{ background: 'hsl(0 0% 5%)', border: '1px solid hsl(0 0% 20%)', boxShadow: '0 30px 80px rgba(0,0,0,0.55)' }}
              >
                {/* Close button */}
                <button
                  onClick={onClose}
                  disabled={isProcessing}
                  className="absolute top-4 right-4 w-[30px] h-[30px] rounded-full inline-flex items-center justify-center z-10 cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: 'rgba(0,0,0,0.45)', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.16)' }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>

                <div className="p-6 md:p-8">
                  {/* Header */}
                  <div className="flex items-center gap-3.5 mb-6">
                    <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0"
                      style={{ background: 'rgba(255,138,115,0.08)', boxShadow: 'inset 0 0 0 1px rgba(255,138,115,0.2)' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ff8a73" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                    </div>
                    <div>
                      <div className="text-[17px] font-semibold tracking-tight">Cancel meeting</div>
                      <div className="text-white/50 text-[12.5px]">This action cannot be undone</div>
                    </div>
                  </div>

                  {/* Meeting info card */}
                  <div className="rounded-xl p-4 mb-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid hsl(0 0% 20% / 0.5)' }}>
                    <div className="text-[14px] font-medium">{meetingTitle}</div>
                    <div className="text-white/50 text-[12.5px] mt-1 flex items-center gap-1.5">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3.5" y="5" width="17" height="15" rx="2"/><path d="M8 3v4M16 3v4M3.5 10h17"/></svg>
                      {meetingDateTime}
                    </div>
                  </div>

                  {/* Policy message */}
                  <div className="rounded-xl p-4 mb-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid hsl(0 0% 20% / 0.5)' }}>
                    <div className="text-[11px] tracking-[2px] uppercase text-white/50 mb-2">Cancellation policy</div>
                    <p className="text-[13px] text-white/80 leading-relaxed whitespace-pre-line">{policyMessage}</p>
                  </div>

                  {/* Optional reason */}
                  <div className="mb-6">
                    <div className="label">Reason for cancellation (optional)</div>
                    <textarea
                      rows={3}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      disabled={isProcessing}
                      className="field"
                      style={{ resize: 'vertical', minHeight: 72, lineHeight: 1.55 }}
                      placeholder="Let us know why you're cancelling..."
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={onClose}
                      disabled={isProcessing}
                      className="btn btn-outline flex-1 justify-center !py-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Keep meeting
                    </button>
                    <button
                      onClick={handleConfirm}
                      disabled={isProcessing}
                      className="btn flex-1 justify-center !py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: '#ff8a73', color: 'hsl(0 0% 0%)', fontWeight: 600 }}
                    >
                      {isProcessing ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                          Cancelling...
                        </span>
                      ) : (
                        'Cancel meeting'
                      )}
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
